import queryString from "query-string"
import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

// Copy to micro-fronted
import { parse, stringify } from "../components/pages/CreateCampaignInfoPage/linkedin-url-parser"
// Copy to micro-fronted
import {
  hasFirstConnections,
  hasGroupConnections,
  hasSecondConnections,
  hasThirdConnections,
  setFirstConnections,
  setGroupConnections,
  setSecondConnections,
  setThirdConnections,
} from "../components/pages/CreateCampaignInfoPage/query-manipulator"
import { LEAD_LIST_SOURCE, PROSPECT_SOURCE_TYPE } from "../constants/lead-list-constants"
import { basicLinkedinLink } from "../constants/linkedin-link-constants"
import * as actions from "../redux/actions"
import campaignService from "../services/campaign-service"
import { getSelectedFacetNetworks } from "../utils/campaign/campaign-utils"
import { useGlobalTranslation } from "../utils/hook"
import {
  decodeFilterURI,
  isNewSalesNavLink,
  isValidLinkedInUrl,
  recognizedLeadListTypeFromUrl,
} from "../utils/lead-list-utils"
import { validateFileSize } from "../utils/validation/validation-utils"

export const useAddNewLeadList = () => {
  const {
    LINKEDIN_SEARCH_RESULT,
    SALES_NAVIGATOR_SEARCH_RESULT,
    SALES_NAVIGATOR_LIST,
    LINKEDIN_POST,
    RECRUITER_SEARCH_RESULT,
    RECRUITER_PIPELINE,
    IMPORT,
  } = LEAD_LIST_SOURCE

  const {
    SALES_NAVIGATOR,
    RECRUITER,
    POST_ENGAGEMENT,
    BASIC,
    LEADS_LIST,
    CSV,
    WEB_HOOK,
    TALENT_LIST,
    MANUALLY_ADDED,
  } = PROSPECT_SOURCE_TYPE

  const [selectedListType, setSelectedListType] = useState("")
  const [listName, setListName] = useState("")
  const [listNameValidationError, setListNameValidationError] = useState(null)
  const [seatId, setSeatId] = useState("")
  const [campaignId, setCampaignId] = useState("")
  const [source, setSource] = useState(null)
  const [isNewListSelected, setIsNewListSelected] = useState(true)

  const [sourceUrl, setSourceUrl] = useState("")
  const [sourceUrlValidationError, setSourceUrlValidationError] = useState(null)
  const [sourceType, setSourceType] = useState("")
  const [sourceAutoReuse, setSourceAutoReuse] = useState(true)
  const [sourceAutoReuseInterval, setSourceAutoReuseInterval] = useState(3600)
  const [sourceCsvFileName, setSourceCsvFileName] = useState("")
  const [sourceCsvFileUploaded, setSourceCsvFileUploaded] = useState(null)
  const [sourceCsvAdditionalInfo, setCsvAdditionalInfo] = useState(null)
  const [sourceCsvErrorMessage, setSourceCsvErrorMessage] = useState(null)

  const [sourceProspects, setSourceProspects] = useState([])
  const [sourceProspectRefreshFrequency, setSourceProspectRefreshFrequency] = useState(10)
  const [sourceProspectOpenProfileStatusOnly, setSourceProspectOpenProfileStatusOnly] =
    useState(true)
  const [sourceProspectPremiumAccountsOnly, setSourceProspectOpenPremiumAccountsOnly] =
    useState(true)
  const [
    sourceProspectRemovePendingConnectionRequests,
    setSourceProspectRemovePendingConnectionRequests,
  ] = useState(true)
  const [sourceRefreshSearchAutomatically, setSourceRefreshSearchAutomatically] = useState(true)

  const [checkboxManuallySelected, setCheckboxManuallySelected] = useState(false)
  const [connections, setConnections] = useState({
    firstChecked: false,
    secondChecked: false,
    thirdChecked: false,
    groupChecked: false,
  })

  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useDispatch()
  const { accountData, accountID, userID } = useSelector(state => ({
    accountData: state.account.accountData,
    accountID: state.account.activeAccountID,
    userID: state.user.profile.id,
  }))

  const { tt } = useGlobalTranslation()

  const initialSourceData = [
    {
      url: sourceUrl,
      type: sourceType,
      status: "ACTIVE",
      pageNumber: 1,
      autoReuse: sourceAutoReuse,
      autoReuseInterval: sourceAutoReuseInterval,
      lastTimeReused: 1716453200,
      lastExecutedAt: 1716453300,
      csvName: sourceCsvFileName,
      scheduledAt: 1716453600,
      prospects: sourceProspects,
      prospectSourceSettings: {
        refreshFrequency: sourceProspectRefreshFrequency,
        openProfileStatusOnly: sourceProspectOpenProfileStatusOnly,
        premiumAccountsOnly: sourceProspectPremiumAccountsOnly,
        removePendingConnectionRequests: sourceProspectRemovePendingConnectionRequests,
        refreshSearchAutomatically: sourceRefreshSearchAutomatically,
      },
    },
  ]

  const submitData = useMemo(() => {
    return {
      listName,
      seatId,
      campaignId,
      sources: source ? [...source] : [{}],
    }
  }, [campaignId, listName, seatId, source])

  const clearCheckboxes = () => {
    setConnections({
      firstChecked: false,
      secondChecked: false,
      thirdChecked: false,
      groupChecked: false,
    })
  }

  const handleBasicLinkedinCheckBoxChanges = checkboxName => {
    switch (checkboxName) {
      case "firstConnection":
        setConnections(prevState => {
          return {
            firstChecked: !prevState.firstChecked,
            secondChecked: false,
            thirdChecked: false,
            groupChecked: prevState.groupChecked,
          }
        })
        break

      case "secondConnection":
        setConnections(prevState => {
          return {
            ...prevState,
            firstChecked: false,
            secondChecked: !prevState.secondChecked,
          }
        })
        break

      case "thirdConnection":
        setConnections(prevState => {
          return {
            ...prevState,
            firstChecked: false,
            thirdChecked: !prevState.thirdChecked,
          }
        })
        break

      case "groupConnection":
        setConnections(prevState => {
          return {
            ...prevState,
            groupChecked: !prevState.groupChecked,
          }
        })
        break

      default:
        break
    }
  }

  const handleCheckboxChanges = checkboxName => {
    setCheckboxManuallySelected(true)
    handleBasicLinkedinCheckBoxChanges(checkboxName)
  }

  const updateListTypeFromSourceUrl = () => {
    const { linkedinSubscriptionId } = accountData
    switch (true) {
      case sourceUrl.includes("linkedin.com/search"):
        if (selectedListType !== LINKEDIN_SEARCH_RESULT) setSelectedListType(LINKEDIN_SEARCH_RESULT)
        break

      case sourceUrl.includes("linkedin.com/sales/lists/people"):
        if (selectedListType !== SALES_NAVIGATOR_LIST) setSelectedListType(SALES_NAVIGATOR_LIST)
        break
      case sourceUrl.includes("linkedin.com/sales") && linkedinSubscriptionId === 2:
        // TODO Ivan: for development purposes.
        // case sourceUrl.includes("linkedin.com/sales") && linkedinSubscriptionId === 1:
        if (selectedListType !== SALES_NAVIGATOR_SEARCH_RESULT)
          setSelectedListType(SALES_NAVIGATOR_SEARCH_RESULT)
        break

      case sourceUrl.includes("linkedin.com/posts"):
        if (selectedListType !== LINKEDIN_POST) setSelectedListType(LINKEDIN_POST)
        break

      case sourceUrl.includes("linkedin.com/talent/search") && linkedinSubscriptionId === 3:
        if (selectedListType !== RECRUITER_SEARCH_RESULT)
          setSelectedListType(RECRUITER_SEARCH_RESULT)
        break

      case sourceUrl.includes("linkedin.com/talent/hire") && linkedinSubscriptionId === 3:
        setSelectedListType("recruiter-pipeline")
        break

      default:
        break
    }
  }

  const deselectAllConnections = () => {
    setConnections({
      firstChecked: false,
      secondChecked: false,
      thirdChecked: false,
      groupChecked: false,
    })
  }

  const setAllConnections = queryObject => {
    let query
    query = setFirstConnections(queryObject, connections.firstChecked)
    query = setSecondConnections(query, connections.secondChecked)
    query = setThirdConnections(query, connections.thirdChecked)
    query = setGroupConnections(query, connections.groupChecked)

    return query
  }

  const recognizedBasicConnection = selectedFacetNetworks => {
    if (!Array.isArray(selectedFacetNetworks)) return

    setConnections(prevState => {
      const updates = { ...prevState }

      selectedFacetNetworks.forEach(facetNetwork => {
        switch (facetNetwork) {
          case "F":
            updates.firstChecked = !prevState.firstChecked
            break
          case "S":
            updates.secondChecked = !prevState.secondChecked
            break
          case "O":
            updates.thirdChecked = !prevState.thirdChecked
            break
          case "A":
            updates.groupChecked = !prevState.groupChecked
            break
          default:
            break
        }
      })

      return updates
    })
  }

  const updateConnectionsFromSourceUrl = () => {
    const newSalesNavUrl =
      isNewSalesNavLink(sourceUrl) && selectedListType === SALES_NAVIGATOR_SEARCH_RESULT
    setCheckboxManuallySelected(false)

    if (newSalesNavUrl) {
      try {
        const parsedState = parse(sourceUrl)
        if (typeof parsedState?.queryObject !== "object") {
          return
        }
        const hasFirstConnection = hasFirstConnections(parsedState.queryObject)
        const hasSecondConnection = hasSecondConnections(parsedState.queryObject)
        const hasThirdConnection = hasThirdConnections(parsedState.queryObject)
        const hasGroupConnection = hasGroupConnections(parsedState.queryObject)

        setConnections({
          firstChecked: hasFirstConnection,
          secondChecked: hasSecondConnection,
          thirdChecked: hasThirdConnection,
          groupChecked: hasGroupConnection,
        })

        if (hasFirstConnection && (hasSecondConnection || hasThirdConnection)) {
          deselectAllConnections()

          parsedState.queryObject = setAllConnections(parsedState.queryObject)
          const stringifiedUrl = stringify(parsedState)

          setSourceUrl(stringifiedUrl)
        }
      } catch {}
    } else {
      const searchString = queryString.parse(
        sourceUrl.includes("?")
          ? sourceUrl.substring(sourceUrl.search(/\?/))
          : sourceUrl.substring(sourceUrl.search(/#/)),
      )
      const { selectedFacetNetworks } = getSelectedFacetNetworks(searchString)
      deselectAllConnections()
      recognizedBasicConnection(selectedFacetNetworks)
    }
  }

  const buildUrl = (newCampaignLink, searchString) => {
    // Check the new Sales Navigator URL
    const newSalesNavUrl = isNewSalesNavLink(newCampaignLink)

    const queryStringData = queryString.stringify(searchString)
    const decodedFilters = decodeFilterURI(queryStringData)

    // Split filters into parts
    const keywordIndex = decodedFilters.indexOf("keywords")
    const firstPart = decodedFilters.substring(0, keywordIndex - 5)
    const sessionPart = decodedFilters.substring(decodedFilters.indexOf("&sessionId"))

    // Build final filter URL
    const finalFilters =
      newSalesNavUrl && decodedFilters.includes("keywords")
        ? `${firstPart}${encodeURI("")}${sessionPart}`
        : `${decodedFilters}${sessionPart}`

    // Extract a base campaign link
    const hasQuery = newCampaignLink.includes("?")
    const baseLink = newCampaignLink.split(hasQuery ? "?" : "#")[0]

    // Build a new campaign link
    let updatedFilters
    if (newSalesNavUrl) {
      if (firstPart) {
        updatedFilters = finalFilters
      } else {
        updatedFilters = decodedFilters
      }
    } else {
      updatedFilters = queryStringData
    }

    newCampaignLink = `${baseLink}${hasQuery ? "?" : "#"}${updatedFilters}`

    // Apply a result
    setSourceUrl(newCampaignLink)
  }

  const leadListCheckboxChangeHandler = () => {
    const newCampaignLink = sourceUrl
    if (!newCampaignLink) {
      return
    }
    const searchParams = newCampaignLink.includes("?")
      ? newCampaignLink.substring(newCampaignLink.search("\\?"))
      : newCampaignLink.substring(newCampaignLink.search("\\#"))

    if (!searchParams) {
      return
    }
    /** newSalesNavUrl constant has been made because
     *  Sales Navigator has a new and old way of creation URLs.
     *  Both are still valid, so we need to handle both :/.
     */
    const newSalesNavUrl = isNewSalesNavLink(newCampaignLink)
    const searchString = queryString.parse(searchParams)

    switch (selectedListType) {
      case LINKEDIN_SEARCH_RESULT:
        // eslint-disable-next-line no-case-declarations
        const facetNetworks = []
        if (connections.firstChecked) {
          facetNetworks.push(basicLinkedinLink.firstConnection)
        } else {
          if (connections.secondChecked) facetNetworks.push(basicLinkedinLink.secondConnection)
          if (connections.thirdChecked) facetNetworks.push(basicLinkedinLink.thirdConnection)
        }

        if (searchString.facetNetwork) {
          searchString.facetNetwork = JSON.stringify(facetNetworks)
        } else {
          searchString.network = JSON.stringify(facetNetworks)
        }
        buildUrl(newCampaignLink, searchString)
        break
      case SALES_NAVIGATOR_SEARCH_RESULT:
        // New Sales navigator URL
        if (newSalesNavUrl) {
          try {
            const val = parse(newCampaignLink)
            let query = val.queryObject
            query = setFirstConnections(query, connections.firstChecked)
            query = setSecondConnections(query, connections.secondChecked)
            query = setThirdConnections(query, connections.thirdChecked)
            query = setGroupConnections(query, connections.groupChecked)

            val.queryObject = query
            const stringifiedUrl = stringify(val)
            setSourceUrl(stringifiedUrl)
            break
          } catch {}
        }
        // Old Sales navigator URL
        else {
          let relationship = ""
          if (connections.firstChecked) {
            relationship += relationship.length > 0 ? ",F" : "F"
          } else {
            if (connections.secondChecked) relationship += relationship.length > 0 ? ",S" : "S"
            if (connections.thirdChecked) relationship += relationship.length > 0 ? ",O" : "O"
          }

          if (connections.groupChecked) relationship += relationship.length > 0 ? ",A" : "A"

          searchString.relationship = relationship
        }
        buildUrl(newCampaignLink, searchString)
        break
      default:
        buildUrl(newCampaignLink, searchString)
        break
    }
  }

  const handleRemoveCsv = () => {
    setSourceCsvErrorMessage(null)
    setSourceCsvFileUploaded(null)
  }

  const handleUploadAndValidateCsv = async file => {
    setIsLoading(true)
    setSourceCsvFileUploaded(file)

    try {
      const response = await campaignService.importValidation(file, userID, accountID)
      const additionalCsvData = response?.data?.result
      if (!additionalCsvData) {
        return
      }
      setCsvAdditionalInfo(additionalCsvData)
    } catch (e) {
      setSourceCsvErrorMessage(e.response?.data?.error?.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleLeadListNameChange = newName => {
    setListNameValidationError(null)
    setListName(newName)
  }

  const handleLeadListTypeSelect = async type => {
    clearCheckboxes()
    setSelectedListType(type)
  }

  const handleIsNewListRadioButtonSelect = () => {
    setIsNewListSelected(prevState => !prevState)
    setSourceUrlValidationError(null)
    setSelectedListType(null)
    setSourceCsvFileUploaded(null)
    setSourceCsvErrorMessage(null)
  }

  // Validators start -------------------------------
  // --- CSV validators
  const isUploadedCsvFileSizeValid = () => {
    if (!sourceCsvFileUploaded) {
      return false
    }
    const fileErrors = validateFileSize(sourceCsvFileUploaded)
    const errors = { ...fileErrors }
    dispatch(actions.setFormErrors(errors))
    if (Object.keys(errors).length !== 0) {
      return false
    }
    return true
  }

  const isCsvFileValid = async () => {
    if (sourceCsvErrorMessage) {
      return false
    }
    // TODO Ivan, check this
    const isCsvSizeValid = isUploadedCsvFileSizeValid()
    if (!isCsvSizeValid) {
      return false
    }
    return true
  }
  // -----

  const areFormFieldsValid = async () => {
    const isNameValid = listName.length > 0
    const isUrlValid = !isNewListSelected ? true : isValidLinkedInUrl(sourceUrl)
    const isUrlRecognizedBySelectedListType =
      selectedListType === recognizedLeadListTypeFromUrl(sourceUrl)

    // Name validation
    if (!isNameValid) {
      setListNameValidationError(tt("name-is-invalid"))
      return false
    }
    // CSV Validation
    if (selectedListType === IMPORT) {
      const isValidFile = await isCsvFileValid()
      return isValidFile
    }
    // URL validations
    if (!isUrlValid) {
      setSourceUrlValidationError(tt("url-is-invalid"))
      return false
    }
    if (!isUrlRecognizedBySelectedListType) {
      setSourceUrlValidationError(tt("url-is-invalid-2"))
      return false
    }

    return true
  }

  // Validators end----------------------------------

  // Listeners start------------------------------
  useEffect(() => {
    if (
      !connections.firstChecked &&
      !connections.secondChecked &&
      !connections.thirdChecked &&
      !connections.groupChecked
    ) {
      setCheckboxManuallySelected(false)
    }
  }, [
    connections.firstChecked,
    connections.groupChecked,
    connections.secondChecked,
    connections.thirdChecked,
  ])

  // Listens for changes in the selected list type (tile) from sourceUrl and updates the connection state accordingly
  useEffect(() => {
    switch (selectedListType) {
      case IMPORT:
        setSourceType(CSV)
        break
      case SALES_NAVIGATOR_SEARCH_RESULT:
        setSourceType(SALES_NAVIGATOR)
        break
      case SALES_NAVIGATOR_LIST:
        setSourceType(LEADS_LIST)
        break
      case RECRUITER_SEARCH_RESULT:
        setSourceType(RECRUITER)
        break
      case RECRUITER_PIPELINE:
        setSourceType(TALENT_LIST)
        break
      case LINKEDIN_POST:
        setSourceType(POST_ENGAGEMENT)
        break
      case LINKEDIN_SEARCH_RESULT:
      default:
        setSourceType(BASIC)
    }
  }, [selectedListType])

  // Listens for CSV upload and changing file name accordingly
  useEffect(() => {
    if (!sourceCsvFileUploaded) {
      setSourceCsvFileName("")
      return
    }
    setSourceCsvFileName(sourceCsvFileUploaded.name)
  }, [sourceCsvFileUploaded])

  // Listens for changes in the selected list type (tile) from sourceUrl and updates the connection state accordingly
  useEffect(() => {
    if (checkboxManuallySelected) {
      return
    }
    updateConnectionsFromSourceUrl()
  }, [checkboxManuallySelected, selectedListType])

  // Detects changes in the sourceUrl and updates the connection selection state based on its type
  useEffect(() => {
    if (checkboxManuallySelected) {
      return
    }
    updateListTypeFromSourceUrl()
  }, [sourceUrl, checkboxManuallySelected])

  // Listens to New List connection manually checkbox changes
  useEffect(() => {
    if (!checkboxManuallySelected) {
      return
    }
    leadListCheckboxChangeHandler()
  }, [selectedListType, checkboxManuallySelected, connections])

  // Listens to Selected List Type changes and change settings accordingly
  useEffect(() => {
    switch (selectedListType) {
      case LINKEDIN_SEARCH_RESULT:
      case SALES_NAVIGATOR_SEARCH_RESULT:
        setSourceProspectOpenPremiumAccountsOnly(false)
        setSourceProspectOpenProfileStatusOnly(false)
        setSourceProspectRemovePendingConnectionRequests(true)
        setSourceRefreshSearchAutomatically(false)
        break
      case LINKEDIN_POST:
      case SALES_NAVIGATOR_LIST:
      case RECRUITER_SEARCH_RESULT:
      default:
        setSourceProspectOpenPremiumAccountsOnly(false)
        setSourceProspectOpenProfileStatusOnly(false)
        setSourceProspectRemovePendingConnectionRequests(false)
        setSourceRefreshSearchAutomatically(false)
        break
    }
  }, [selectedListType])

  // Preparing New List Submit Data
  useEffect(() => {
    setSource(initialSourceData)
  }, [
    sourceUrl,
    sourceType,
    sourceAutoReuse,
    sourceAutoReuseInterval,
    sourceCsvFileName,
    sourceProspects,
    sourceProspectRefreshFrequency,
    sourceProspectOpenProfileStatusOnly,
    sourceProspectPremiumAccountsOnly,
    sourceRefreshSearchAutomatically,
    sourceProspectRemovePendingConnectionRequests,
  ])
  // Listeners end --------------------------

  return {
    submitData,
    selectedListType,
    clearCheckboxes,
    isLoading,
    isNewListSelected,
    areFormFieldsValid,

    handleCheckboxChanges,
    handleUploadAndValidateCsv,
    handleRemoveCsv,
    handleLeadListNameChange,
    handleLeadListTypeSelect,
    handleIsNewListRadioButtonSelect,

    listNameValidationError,
    sourceUrlValidationError,
    sourceCsvErrorMessage,
    setListNameValidationError,
    setSourceUrlValidationError,

    listName,
    seatId,
    campaignId,
    connections,
    source,
    sourceUrl,
    sourceType,
    sourceAutoReuse,
    sourceAutoReuseInterval,
    sourceCsvFileName,
    sourceProspects,
    sourceProspectRefreshFrequency,
    sourceProspectOpenProfileStatusOnly,
    sourceProspectPremiumAccountsOnly,
    sourceRefreshSearchAutomatically,
    sourceProspectRemovePendingConnectionRequests,
    sourceCsvFileUploaded,
    sourceCsvAdditionalInfo,

    setSelectedListType,
    setListName,
    setSeatId,
    setCampaignId,
    setSource,
    setSourceUrl,
    setSourceType,
    setSourceAutoReuse,
    setSourceAutoReuseInterval,
    setSourceCsvFileName,
    setSourceProspects,
    setSourceProspectRefreshFrequency,
    setSourceProspectOpenProfileStatusOnly,
    setSourceProspectOpenPremiumAccountsOnly,
    setSourceProspectRemovePendingConnectionRequests,
    setSourceRefreshSearchAutomatically,
    setIsNewListSelected,
    setSourceCsvFileUploaded,
  }
}
